import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { StatItem } from '@/design/StatBand';
import type { SignalHeat } from '@/shared/signals/SignalTypes';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';

/**
 * Backs the Governance › Risk Density screen. Loads the department × work-relatedness
 * activity grid (a proxy for where governance exposure clusters) from the interactions
 * OLAP, and derives the side-rail summary stats and takeaways from the same matrix —
 * one query feeds both the heatmap and its rail. A failed or empty load leaves an
 * empty grid.
 */
@injectable()
export class GovernanceRiskDensityViewModel extends BaseViewModel {
    loading = true;
    loaded = false;
    heat: SignalHeat | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway) {
        super();
    }

    async load(filters: AnalyticsFilter[]): Promise<void> {
        this.loading = true;
        this.notifyChange();
        try {
            const res = await this.gateway.aggregate({ source: 'interactions', groupBy: ['user_department', 'work_related'], metrics: ['turns'], filters });
            const cols = [...new Set(res.rows.map((row) => row.keys.work_related).filter((value): value is string => typeof value === 'string' && value !== '—'))].slice(0, 5);
            const byRow = new Map<string, Map<string, number>>();
            for (const row of res.rows) {
                const rowKey = row.keys.user_department;
                const colKey = row.keys.work_related;
                if (rowKey === undefined || colKey === undefined || rowKey === '—' || colKey === '—') continue;
                const cells = byRow.get(rowKey) ?? new Map<string, number>();
                cells.set(colKey, (cells.get(colKey) ?? 0) + row.metrics.turns);
                byRow.set(rowKey, cells);
            }
            const rowLabels = [...byRow.keys()].slice(0, 8);
            const rows = rowLabels.map((label) => ({ label, values: cols.map((col) => byRow.get(label)?.get(col) ?? 0) }));
            this.heat = rows.length > 0 && cols.length > 0 ? { columns: cols, rows, format: 'count' } : null;
        } catch {
            this.heat = null;
        } finally {
            this.loading = false;
            this.loaded = true;
            this.notifyChange();
        }
    }

    /** Side-rail summary: total activity, departments in view, and the densest department. */
    get summary(): StatItem[] {
        const heat = this.heat;
        if (heat === null) return [];
        const rowTotals = heat.rows.map((row) => ({ label: row.label, total: row.values.reduce((sum, value) => sum + value, 0) }));
        const grandTotal = rowTotals.reduce((sum, row) => sum + row.total, 0);
        const densest = rowTotals.reduce((max, row) => (row.total > max.total ? row : max), { label: '—', total: 0 });
        return [
            { label: 'Classified prompts', value: grandTotal.toLocaleString('en-US'), hint: 'selected range' },
            { label: 'Departments', value: heat.rows.length.toLocaleString('en-US') },
            { label: 'Densest area', value: densest.label },
        ];
    }

    /** Client-derived takeaways shown in the Insights rail card. */
    get callouts(): TopicCallout[] {
        const heat = this.heat;
        if (heat === null) return [];
        const out: TopicCallout[] = [];
        const rowTotals = heat.rows.map((row) => ({ label: row.label, total: row.values.reduce((sum, value) => sum + value, 0) }));
        const grandTotal = rowTotals.reduce((sum, row) => sum + row.total, 0);
        const densest = rowTotals.reduce((max, row) => (row.total > max.total ? row : max), { label: '—', total: 0 });
        if (densest.total > 0 && grandTotal > 0) {
            out.push({ tone: 'headline', text: `${densest.label} shows the densest AI activity — ${Math.round((densest.total / grandTotal) * 100)}% of classified prompts in view.` });
        }
        const personalCol = heat.columns.findIndex((col) => col.toLowerCase() === 'personal');
        if (personalCol >= 0) {
            const personalLeader = heat.rows.reduce((max, row) => ((row.values[personalCol] ?? 0) > max.value ? { label: row.label, value: row.values[personalCol] ?? 0 } : max), { label: '—', value: 0 });
            if (personalLeader.value > 0) {
                out.push({ tone: 'watch', text: `Personal-use activity concentrates in ${personalLeader.label} — worth a closer look for governance exposure.` });
            }
        }
        return out;
    }
}
